import { withDependencies } from '@wix/thunderbolt-ioc'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import { IViewerManagerImplementor } from 'thunderbolt-viewer-manager-types'
import { BrowserWindow, BrowserWindowSymbol, CarmiInstance } from '@wix/thunderbolt-symbols'
import type { IEditorAnimations } from 'feature-animations'
import { EditorAnimationsSym } from 'feature-animations'
import { Catharsis, CatharsisSymbol } from '@wix/thunderbolt-catharsis'
import { convertEffectToAnimationDef } from '@wix/thunderbolt-commons'
import { TimeAnimationOptions, AnimationDef, TriggerVariant, ScrubAnimationDef } from '@wix/thunderbolt-becky-types'

const viewerManagerAnimationApiFactory = (
	browserWindow: NonNullable<BrowserWindow>,
	animationManager: IEditorAnimations,
	{ renderFlags: renderFlagsAPI }: DSCarmi,
	catharsis: Catharsis
): IViewerManagerImplementor<'animation'> => {
	return {
		animation: {
			reloadPageAnimations: () => {
				// remove this after merging specs.thunderbolt.comps_to_hide_catharsis
				renderFlagsAPI.setRenderFlag('showScreenInComp', false)
				catharsis.transaction(({ updateEnvironment }) =>
					updateEnvironment({ renderFlags: { showScreenInComp: false } })
				)
				/*
				todo - need to implement after adding preview animations (TB-3098)
				 */
			},
			stopAndClearAllAnimations: () => {
				animationManager.stopAllPreviewAnimations()
				// remove this after merging specs.thunderbolt.comps_to_hide_catharsis
				renderFlagsAPI.setRenderFlag('showScreenInComp', true)
				catharsis.transaction(({ updateEnvironment }) =>
					updateEnvironment({ renderFlags: { showScreenInComp: true } })
				)
				/*
					todo - need to implement after adding preview animations (TB-3098):
					stop all running animations (screenIn, scrub...)
					prevent animations (screenIn, Scrub) -> should be able to disable/enable this options and should be part disabled by default
				 */
			},
			// @ts-ignore
			updateScrubAnimationsPreview: (
				animations: Record<string, ScrubAnimationDef>,
				triggers: Record<string, TriggerVariant>
			) => animationManager.updateScrubAnimationsPreview(animations, triggers),
			// @ts-ignore
			previewScrubAnimations: (
				animations: Record<string, ScrubAnimationDef>,
				triggers: Record<string, TriggerVariant>
			) => animationManager.startScrubPreview(animations, triggers),
			stopPreviewScrubAnimations: () => animationManager.stopScrubPreview(),
			previewAnimation: (id, pageId, animationDef, onComplete) => {
				const element = browserWindow.document.getElementById(id)

				if (!element) {
					return null
				}

				const isMotionEffect = !!(
					(animationDef as TimeAnimationOptions).namedEffect ||
					(animationDef as TimeAnimationOptions).keyframeEffect
				)
				const animation = (isMotionEffect
					? convertEffectToAnimationDef(animationDef as TimeAnimationOptions)
					: animationDef) as AnimationDef

				return animationManager.runPreviewAnimation(id, animation, onComplete, isMotionEffect)
			},
			previewTransition: (srcIds: Array<string>, targetIds: Array<string>, pageId, animationDef, onComplete) => {
				const srcIdsFound = srcIds.filter((id) => browserWindow.document.getElementById(id))
				const targetIdsFound = targetIds.filter((id) => browserWindow.document.getElementById(id))
				if (srcIdsFound.length === 0 && targetIdsFound.length === 0) {
					onComplete()
					return null
				}

				return animationManager.runPreviewTransition(srcIdsFound, targetIdsFound, animationDef, onComplete)
			},
			stopPreviewAnimation: (sequenceId) => {
				animationManager.stopPreviewAnimation(sequenceId)
			},
			getSiteAnimationsService: () => animationManager.getPreviewAnimationsService(),
		},
	}
}
export const ViewerManagerAnimationApi = withDependencies(
	[BrowserWindowSymbol, EditorAnimationsSym, CarmiInstance, CatharsisSymbol],
	viewerManagerAnimationApiFactory
)
